import { useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Send, X } from 'lucide-react';
import { formatEuro } from '../../utils/currency';
import { invoiceRequestService, type InvoiceRequest } from '../../services/invoiceRequestService';
import { useModal } from '../../contexts/ModalContext';
import type { Client } from '../../types/client';

const round2 = (n: number) => Math.round(n * 100) / 100;

/** `total` split in `count` parts; the last one takes the cents left over. */
export function splitAmount(total: number, count: number): number[] {
  if (count < 1) return [];
  const share = Math.floor((total / count) * 100) / 100;
  const parts = Array.from({ length: count }, () => share);
  parts[count - 1] = round2(total - share * (count - 1));
  return parts;
}

interface Props {
  campaignId: string | null;
  projectoId?: string | null;
  campaignName: string;
  client: Client | null;
  /** Campaign value without VAT; 0 when not set. */
  totalAmount: number;
  /** Issued or paid invoices already on the campaign. */
  invoicedAmount: number;
  /** Asked for and not yet received. */
  requestedAmount: number;
  /** Planned invoices (not issued, no number) to start from. */
  plannedAmounts: number[];
  defaultPurchaseOrder: string;
  onClose: () => void;
  onCreated: (request: InvoiceRequest) => void;
}

export function InvoiceRequestModal({
  campaignId,
  projectoId,
  campaignName,
  client,
  totalAmount,
  invoicedAmount,
  requestedAmount,
  plannedAmounts,
  defaultPurchaseOrder,
  onClose,
  onCreated,
}: Props) {
  const { toast } = useModal();
  const remaining = totalAmount > 0 ? Math.max(0, round2(totalAmount - invoicedAmount - requestedAmount)) : 0;
  const [amounts, setAmounts] = useState<number[]>(() => {
    const planned = plannedAmounts.filter(a => a > 0);
    if (planned.length > 0 && (totalAmount === 0 || round2(planned.reduce((s, a) => s + a, 0)) <= remaining + 0.01)) return planned;
    return [remaining > 0 ? remaining : 0];
  });
  const [purchaseOrder, setPurchaseOrder] = useState(defaultPurchaseOrder);
  const [notes, setNotes] = useState('');
  const [sending, setSending] = useState(false);

  const requestTotal = round2(amounts.reduce((s, a) => s + (a || 0), 0));
  const over = totalAmount > 0 && requestTotal > remaining + 0.01;
  const hasEmpty = amounts.some(a => !a || a <= 0);
  const missingClientData = !client ? ['cliente'] : [!client.vat_number && 'NIF', !client.address && 'morada'].filter(Boolean) as string[];

  const setCount = (count: number) => {
    const next = Math.min(12, Math.max(1, count));
    const base = totalAmount > 0 ? remaining : requestTotal;
    setAmounts(base > 0 ? splitAmount(base, next) : Array.from({ length: next }, (_, i) => amounts[i] || 0));
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Rendered inside the campaign form's React tree: keep the submit here
    e.stopPropagation();
    if (hasEmpty || over) return;
    setSending(true);
    try {
      const request = await invoiceRequestService.create({
        campaign_id: campaignId,
        projecto_id: projectoId ?? null,
        campaign_name: campaignName,
        client_name: client?.name ?? null,
        client_vat: client?.vat_number ?? null,
        client_address: client?.address ?? null,
        purchase_order: purchaseOrder.trim() || null,
        notes: notes.trim() || null,
        amounts: amounts.map(round2),
      });
      toast.success(amounts.length === 1 ? 'Fatura pedida' : `${amounts.length} faturas pedidas`);
      onCreated(request);
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : 'Não foi possível enviar o pedido');
    } finally {
      setSending(false);
    }
  }

  return createPortal(
    <div className="fixed inset-0 bg-brand-ink/50 backdrop-blur-sm flex items-center justify-center z-[70] p-4" role="dialog" aria-modal="true" aria-label="Pedir fatura">
      <form onSubmit={handleSubmit} className="bg-white rounded-card shadow-float max-w-lg w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="mo-display text-lg text-brand-ink">Pedir fatura</h2>
            <p className="text-xs text-gray-500 truncate">{campaignName}</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-gray-100" aria-label="Fechar">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 overflow-y-auto">
          <section className="rounded-xl bg-gray-50 border border-gray-200 p-3 text-sm space-y-0.5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Faturar a</p>
            <p className="font-semibold text-brand-ink">{client?.name || 'Sem cliente associado'}</p>
            {client?.vat_number && <p className="text-gray-600">NIF {client.vat_number}</p>}
            {client?.address && <p className="text-gray-600">{client.address}</p>}
            {missingClientData.length > 0 && (
              <p className="flex items-center gap-1.5 text-xs text-amber-700 pt-1">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                Falta {missingClientData.join(' e ')} na ficha do cliente. Podes pedir na mesma e indicar na nota.
              </p>
            )}
          </section>

          {totalAmount > 0 && (
            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
              {[
                [projectoId ? 'Valor do projeto' : 'Valor da campanha', totalAmount],
                ['Já faturado', invoicedAmount],
                ['Já pedido', requestedAmount],
                ['Por faturar', remaining],
              ].map(([label, value]) => (
                <div key={label as string} className="rounded-xl border border-gray-200 px-2 py-2">
                  <dt className="text-[10px] font-medium uppercase tracking-wide text-gray-500">{label}</dt>
                  <dd className="text-sm font-bold tabular-nums text-brand-ink">{formatEuro(value as number)}</dd>
                </div>
              ))}
            </dl>
          )}

          <section className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <label className="cm-label">Quantas faturas?</label>
              <div className="flex items-center gap-1.5">
                <button type="button" onClick={() => setCount(amounts.length - 1)} disabled={amounts.length <= 1} className="w-7 h-7 rounded-full border border-gray-300 text-sm disabled:opacity-40" aria-label="Menos uma fatura">−</button>
                <span className="w-6 text-center text-sm font-semibold tabular-nums" aria-live="polite">{amounts.length}</span>
                <button type="button" onClick={() => setCount(amounts.length + 1)} disabled={amounts.length >= 12} className="w-7 h-7 rounded-full border border-gray-300 text-sm disabled:opacity-40" aria-label="Mais uma fatura">+</button>
              </div>
            </div>
            <div className="space-y-2">
              {amounts.map((amount, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-sm text-gray-600 w-24 flex-shrink-0">Fatura {i + 1}{amounts.length > 1 ? `/${amounts.length}` : ''}</span>
                  <div className="relative flex-1">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      required
                      value={amount || ''}
                      onChange={e => setAmounts(prev => prev.map((a, j) => j === i ? parseFloat(e.target.value) || 0 : a))}
                      className="cm-input cm-numeric pr-8"
                      aria-label={`Valor da fatura ${i + 1}`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">€</span>
                  </div>
                </div>
              ))}
            </div>
            {totalAmount > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                <button type="button" onClick={() => setAmounts([remaining])} className="cm-btn cm-btn-ghost cm-btn-sm">Tudo numa fatura</button>
                {amounts.length > 1 && (
                  <button type="button" onClick={() => setAmounts(splitAmount(remaining, amounts.length))} className="cm-btn cm-btn-ghost cm-btn-sm">Dividir em partes iguais</button>
                )}
              </div>
            )}
            <p className={`text-xs ${over ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
              Total pedido {formatEuro(requestTotal)} sem IVA
              {totalAmount > 0 && (over
                ? ` · passa o que falta faturar em ${formatEuro(round2(requestTotal - remaining))}`
                : ` · fica por faturar ${formatEuro(round2(remaining - requestTotal))}`)}
            </p>
          </section>

          <div>
            <label className="cm-label" htmlFor="ir-po">Ordem de compra</label>
            <input id="ir-po" type="text" value={purchaseOrder} onChange={e => setPurchaseOrder(e.target.value)} className="cm-input mt-1" placeholder="Opcional" />
          </div>

          <div>
            <label className="cm-label" htmlFor="ir-notes">Nota para quem emite</label>
            <textarea id="ir-notes" value={notes} onChange={e => setNotes(e.target.value)} rows={3} className="cm-textarea mt-1 w-full" placeholder="Descrição a usar, contacto para envio, datas…" />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className="cm-btn cm-btn-ghost">Cancelar</button>
          <button type="submit" disabled={sending || hasEmpty || over} className="cm-btn cm-btn-primary">
            <Send className="w-4 h-4" />
            {sending ? 'A enviar…' : 'Enviar pedido'}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}
